import axios, { client2 } from "@/config/axios-wrapper";
import { ApplyRequest, Job } from "@/types/job";
import { JobApplicationBasic, JobApplicationStatus } from "@/types/job_application";
import queryString from "query-string";

export const getJobById = async (jobId: string) => {
  const response = await axios.get<Job>(`/jobs/${jobId}`);
  return response.data;
};

export const createJob = async (job) => {
  const response = await axios.post<{
    id: string;
    active: boolean;
    alt_id: string;
    benefits: string[];
    address: {
      line1: string;
      line2: string;
      country: string;
      state: string;
      city: string;
      zip_code: string;
    };
    details: string;
    experience_years: { min: number; max: number };
    industry: string;
    organization: { id: string; name: string; logo: string; details: string };
    salary_type: string;
    salary: { min: number; max: number };
    skills: string[];
    title: string;
    type: string;
    video: string;
    work_preference: string;
    created_at: string;
  }>("/jobs", job);
  return response.data;
};

export const updateJob = async (jobId: string, job) => {
  const response = await axios.put<Job>(`/jobs/${jobId}`, job);
  return response.data;
};

export const deleteJob = async (jobId: string) => {
  const response = await axios.delete(`/jobs/${jobId}`);
  return response.data;
};

export const updateJobVideo = async (jobId: string, path: string) => {
  const response = await axios.post(`/jobs/${jobId}/video`, { path });
  return response.data;
};

export const updateJobStatus = async (jobId: string, active: boolean) => {
  const response = await axios.put(`/jobs/${jobId}/active`, { active });
  return response.data;
};

export const deleteJobVideo = async (jobId: string) => {
  const response = await axios.delete(`/jobs/${jobId}/video`);
  return response.data;
};

export const archieveJob = async (userId: string, jobId: string) => {
  const response = await axios.post(`users/${userId}/archived/${jobId}`);
  return response.data;
};

export const getArchivedJobs = async (userId: string) => {
  const response = await axios.get(`/users/${userId}/archived`);
  return response.data;
};

export const unArchivedJob = async (userId: string, jobId: string) => {
  const response = await axios.delete(`/users/${userId}/archived/${jobId}`);
  return response.data;
};

export const getJobMatchingPercentageDetails = async (jobseekerId: string, jobIds: string[]) => {
  const payload = {
    job_ids: jobIds,
  };
  const response = await client2.post(`jobseekers/${jobseekerId}/matchingDetails`, payload);
  return response.data;
};

export const getJobApplyRequests = async (jobId: string) => {
  const response = await client2.get<{
    result: ApplyRequest[];
  }>(`/jobs/${jobId}/apply-requests`);
  return response.data;
};

export const sendApplyRequest = async (jobId: string, jobseeker_id: string) => {
  const payload = { jobseeker_id };
  const response = await client2.post(`/jobs/${jobId}/apply-requests/`, payload);
  return response.data;
};

export const cancelApplyRequest = async (jobId: string, applyRequestId: string) => {
  const response = await client2.post(`/jobs/${jobId}/apply-requests/${applyRequestId}/cancel`);
  return response.data;
};

export const declineApplyRequest = async (jobId: string, applyRequestId: string) => {
  const response = await client2.post(`/jobs/${jobId}/apply-requests/${applyRequestId}/decline`);
  return response.data;
};

export const getJobApplications = async (
  jobId: string,
  {
    query,
    sort,
    status,
    radius,
    country,
    state,
    city,
    experience_years,
  }: {
    query: string;
    status: JobApplicationStatus[];
    sort: "top_rated" | "new_to_old" | "old_to_new";
    radius: number;
    country: string;
    state: string;
    city: string;
    experience_years: { min: number; max: number };
  },
) => {
  const queryParams = {
    query,
    status,
    sort,
    radius: radius === 0 ? null : radius,
    country,
    state,
    city,
    experience:
      experience_years.min === 0 && experience_years.max === 0
        ? null
        : JSON.stringify(experience_years),
  };

  const queryStringParams = queryString.stringify(queryParams, {
    skipEmptyString: true,
    skipNull: true,
  });

  const response = await axios.get<{ applications: JobApplicationBasic[] }>(
    `/jobs/${jobId}/applications?${queryStringParams}`,
  );
  return response.data;
};

export const deleteJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axios.post(`/jobs/${jobId}/applications/${applicationId}/delete`);
  return response.data;
};

export const shortlistJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axios.post(`/jobs/${jobId}/applications/${applicationId}/shortlist`);
  return response.data;
};

export const restoreJobApplication = async ({
  jobId,
  applicationId,
}: {
  jobId: string;
  applicationId: string;
}) => {
  const response = await axios.post(`/jobs/${jobId}/applications/${applicationId}/restore`);
  return response.data;
};
